import type { StockfishStatus } from "@/pages/analysis/engine/stockfishTypes";
import { useI18n } from "@/shared/i18n/LanguageContext";

export type StockfishPanelLine = {
  rank: number;
  eval: string;
  variation: string;
  hasPv: boolean;
};

type StockfishLockState = {
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
};

type StockfishAnalysisPanelProps = {
  enabled: boolean;
  status: StockfishStatus;
  scoreLabel: string;
  lines: StockfishPanelLine[];
  expandedLine: number | null;
  showAllLines: boolean;
  locked?: StockfishLockState;
  onToggleEnabled: () => void;
  onToggleLine: (rank: number) => void;
  onToggleShowAllLines: () => void;
};

export function StockfishAnalysisPanel({
  enabled,
  status,
  scoreLabel,
  lines,
  expandedLine,
  showAllLines,
  locked,
  onToggleEnabled,
  onToggleLine,
  onToggleShowAllLines,
}: StockfishAnalysisPanelProps) {
  const { copy } = useI18n();
  return (
    <section
      className={locked ? "analysis-engine-panel analysis-engine-panel--locked" : "analysis-engine-panel"}
      aria-label={copy.analysis.stockfish}
    >
      <div className="analysis-engine-panel__top">
        <button
          type="button"
          className="analysis-engine-toggle"
          data-status={locked ? "off" : enabled ? status : "off"}
          aria-label={!locked && enabled ? copy.analysis.turnStockfishOff : copy.analysis.turnStockfishOn}
          aria-pressed={!locked && enabled}
          disabled={Boolean(locked)}
          onClick={onToggleEnabled}
        >
          <span />
        </button>
        <strong className="analysis-engine-score">{locked ? copy.analysis.locked : scoreLabel}</strong>
      </div>

      {locked ? (
        <div className="analysis-engine-locked" role="status">
          <strong>{locked.title}</strong>
          {locked.message ? <span>{locked.message}</span> : null}
          {locked.actionLabel && locked.onAction ? (
            <button type="button" onClick={locked.onAction}>
              {locked.actionLabel}
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="analysis-engine-lines">
            {lines.map((line) => (
              <div
                className={
                  expandedLine === line.rank
                    ? "analysis-engine-line analysis-engine-line--expanded"
                    : "analysis-engine-line"
                }
                key={line.rank}
              >
                <strong>{line.eval}</strong>
                <span>{line.variation}</span>
                <button
                  type="button"
                  disabled={!line.hasPv}
                  aria-label={`${copy.analysis.expandLine} ${line.rank}`}
                  onClick={() => onToggleLine(line.rank)}
                >
                  {expandedLine === line.rank ? "▴" : "▾"}
                </button>
              </div>
            ))}
          </div>
          {enabled ? (
            <button
              className="analysis-engine-more"
              type="button"
              onClick={onToggleShowAllLines}
            >
              {showAllLines ? copy.analysis.top3 : copy.analysis.moreLines}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
